import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Form, Input, Modal, Select, Space, Spin, Tabs, Tag } from 'antd';
import { toast } from 'react-toastify';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { authState, authInfoState, modalState } from '../../recoils';
import PageHeader from 'src/components/PageHeader/PageHeader';
import DeskPanel from 'src/components/DeskPanel/DesktPanel';
import Button from 'src/components/Button/Button';
import Icon from 'src/components/Icon/Icon';
import { getUserList, UserInfo } from '../../services/userList';
import {
  getAllScopes,
  createScope,
  getPositionScopes,
  getUserScopes,
  updatePositionScopes,
  updateUserScopes,
  ScopeItem,
  PositionScopeItem,
} from '../../services/scopeManagement';
import { groupScopes, getActionIcon, getScopeLabel } from './scopeGroups';

const { TabPane } = Tabs;

const PositionTag = () => (
  <Tag color="blue" style={{ marginInlineEnd: 0 }}>ตำแหน่ง</Tag>
);

const GroupedScopeSelector = ({
  scopes,
  checkedUids,
  positionUids,
  onChange,
}: {
  scopes: ScopeItem[];
  checkedUids: string[];
  positionUids?: string[];
  onChange: (uids: string[]) => void;
}) => {
  const groups = useMemo(() => groupScopes(scopes), [scopes]);
  const checkedSet = useMemo(() => new Set(checkedUids), [checkedUids]);

  const toggle = (uid: string, next: boolean) => {
    const updated = new Set(checkedUids);
    if (next) {
      updated.add(uid);
    } else {
      updated.delete(uid);
    }
    onChange(Array.from(updated));
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {groups.map((group) => (
        <div
          key={group.key}
          style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 16px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 12 }}>
            <group.Icon />
            {group.label}
          </div>

          {group.pages.length > 0 && (
            <Space direction="vertical" size={4} style={{ marginBottom: group.actions.length > 0 ? 12 : 0 }}>
              {group.pages.map((page) => (
                <Checkbox
                  key={page.uid}
                  checked={checkedSet.has(page.uid)}
                  onChange={(e) => toggle(page.uid, e.target.checked)}
                >
                  <Space size={4}>
                    <span title={page.description ? `${page.name} — ${page.description}` : page.name}>
                      {getScopeLabel(page.name)}
                    </span>
                    {positionUids?.includes(page.uid) && <PositionTag />}
                  </Space>
                </Checkbox>
              ))}
            </Space>
          )}

          {group.actions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {group.actions.map((action) => {
                const ActionIcon = getActionIcon(action.name);
                return (
                  <Tag.CheckableTag
                    key={action.uid}
                    checked={checkedSet.has(action.uid)}
                    onChange={(next) => toggle(action.uid, next)}
                    style={{ border: '1px solid #d9d9d9', padding: '4px 10px', userSelect: 'none' }}
                  >
                    <Space size={4}>
                      <ActionIcon />
                      <span title={action.description ? `${action.name} — ${action.description}` : action.name}>
                        {getScopeLabel(action.name)}
                      </span>
                      {positionUids?.includes(action.uid) && <PositionTag />}
                    </Space>
                  </Tag.CheckableTag>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </Space>
  );
};

const ByPositionTab = ({
  scopes,
  token,
  currentRole,
}: {
  scopes: ScopeItem[];
  token: string;
  currentRole: string;
}) => {
  const [positions, setPositions] = useState<PositionScopeItem[]>([]);
  const [selectedPositionUid, setSelectedPositionUid] = useState<string>('');
  const [checkedUids, setCheckedUids] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const setModal = useSetRecoilState(modalState);

  const loadPositions = () => {
    getPositionScopes(token).then((data) => {
      const list = data || [];
      setPositions(list);
      if (list.length > 0 && !selectedPositionUid) {
        setSelectedPositionUid(list[0].uid);
        setCheckedUids(list[0].scope_uids);
      }
    });
  };

  useEffect(() => {
    loadPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSelectPosition = (uid: string) => {
    setSelectedPositionUid(uid);
    const position = positions.find((p) => p.uid === uid);
    setCheckedUids(position?.scope_uids || []);
  };

  const doSave = async () => {
    if (!selectedPositionUid) return;
    setSaving(true);
    const success = await updatePositionScopes(selectedPositionUid, checkedUids, token);
    setSaving(false);
    if (success) {
      toast.success('บันทึก scope ของตำแหน่งสำเร็จ พนักงานในตำแหน่งนี้จะถูกอัปเดต');
      loadPositions();
    } else {
      toast.warning('บันทึกไม่สำเร็จ');
    }
  };

  const handleSave = () => {
    if (!selectedPositionUid) return;
    const selectedPosition = positions.find((p) => p.uid === selectedPositionUid);
    const affectsMe = !!selectedPosition && selectedPosition.name === currentRole;
    setModal({
      isModalVisible: true,
      type: affectsMe ? 'error' : 'warning',
      buttonType: 'question',
      title: 'ยืนยันการบันทึก',
      confirmText: 'บันทึก',
      content: affectsMe
        ? `การเปลี่ยน scope ของตำแหน่ง "${selectedPosition?.name}" รวมถึงบัญชีของคุณด้วย เมื่อบันทึกแล้วเซสชันของคุณจะถูกรีเซ็ตและต้องเข้าสู่ระบบใหม่ ต้องการดำเนินการต่อหรือไม่?`
        : `พนักงานทุกคนในตำแหน่ง "${selectedPosition?.name}" จะถูกรีเซ็ตเซสชันและต้องเข้าสู่ระบบใหม่ในการใช้งานครั้งถัดไป ต้องการดำเนินการต่อหรือไม่?`,
      onConfirm: () => doSave(),
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space>
        <span>เลือกตำแหน่ง:</span>
        <Select
          style={{ minWidth: 220 }}
          value={selectedPositionUid || undefined}
          placeholder="เลือกตำแหน่ง"
          onChange={handleSelectPosition}
          options={positions.map((p) => ({ label: p.name, value: p.uid }))}
        />
      </Space>
      <div style={{ color: '#888' }}>
        การแก้ไข scope ของตำแหน่งจะอัปเดต scope ของพนักงานทุกคนในตำแหน่งนี้
        (ยกเว้น scope ที่ถูกตั้งค่าเฉพาะบุคคลไว้)
      </div>
      <GroupedScopeSelector scopes={scopes} checkedUids={checkedUids} onChange={setCheckedUids} />
      <Button type="primary" loading={saving} disabled={!selectedPositionUid} onClick={handleSave}>
        บันทึก
      </Button>
    </Space>
  );
};

const ByEmployeeTab = ({
  scopes,
  token,
  currentUserUid,
}: {
  scopes: ScopeItem[];
  token: string;
  currentUserUid: string;
}) => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [selectedUserUid, setSelectedUserUid] = useState<string>('');
  const [checkedUids, setCheckedUids] = useState<string[]>([]);
  const [positionUids, setPositionUids] = useState<string[]>([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const setModal = useSetRecoilState(modalState);

  useEffect(() => {
    getUserList(token).then((data) => {
      setUsers(data?.results || []);
    });
  }, [token]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        label: `${u.personal_number} · ${u.name || '—'} · ${u.email} (${u.role})`,
        value: u.uid,
      })),
    [users],
  );

  const handleSelectUser = (uid: string) => {
    setSelectedUserUid(uid);
    setLoadingUser(true);
    getUserScopes(uid, token).then((detail) => {
      setLoadingUser(false);
      if (detail) {
        setCheckedUids(detail.scope_uids);
        setPositionUids(detail.position_scope_uids);
      } else {
        setCheckedUids([]);
        setPositionUids([]);
        toast.warning('โหลดข้อมูล scope ของพนักงานไม่สำเร็จ');
      }
    });
  };

  const doSave = async () => {
    if (!selectedUserUid) return;
    setSaving(true);
    const success = await updateUserScopes(selectedUserUid, checkedUids, token);
    setSaving(false);
    if (success) {
      toast.success('บันทึก scope ของพนักงานสำเร็จ');
      handleSelectUser(selectedUserUid);
    } else {
      toast.warning('บันทึกไม่สำเร็จ');
    }
  };

  const handleSave = () => {
    if (!selectedUserUid) return;
    const affectsMe = selectedUserUid === currentUserUid;
    setModal({
      isModalVisible: true,
      type: affectsMe ? 'error' : 'warning',
      buttonType: 'question',
      title: 'ยืนยันการบันทึก',
      confirmText: 'บันทึก',
      content: affectsMe
        ? 'คุณกำลังแก้ scope ของบัญชีตัวเอง เมื่อบันทึกแล้วเซสชันของคุณจะถูกรีเซ็ตและต้องเข้าสู่ระบบใหม่ ต้องการดำเนินการต่อหรือไม่?'
        : 'พนักงานคนนี้จะถูกรีเซ็ตเซสชันและต้องเข้าสู่ระบบใหม่ในการใช้งานครั้งถัดไป ต้องการดำเนินการต่อหรือไม่?',
      onConfirm: () => doSave(),
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space>
        <span>เลือกพนักงาน:</span>
        <Select
          style={{ minWidth: 480 }}
          showSearch
          value={selectedUserUid || undefined}
          placeholder="เลือกพนักงาน (ค้นหาด้วยชื่อ / รหัส / email)"
          optionFilterProp="label"
          onChange={handleSelectUser}
          options={userOptions}
        />
      </Space>
      <div style={{ color: '#888' }}>
        scope ที่ติดแท็ก <Tag color="blue">ตำแหน่ง</Tag> มาจากตำแหน่งของพนักงาน
        ส่วน scope อื่นที่เลือกเพิ่มจะถูกบันทึกเป็นค่าเฉพาะบุคคลและจะไม่ถูกลบเมื่อเปลี่ยนตำแหน่ง
      </div>
      {selectedUserUid ? (
        <Spin spinning={loadingUser}>
          <GroupedScopeSelector
            scopes={scopes}
            checkedUids={checkedUids}
            positionUids={positionUids}
            onChange={setCheckedUids}
          />
        </Spin>
      ) : (
        <div style={{ color: '#888' }}>กรุณาเลือกพนักงานเพื่อจัดการ scope</div>
      )}
      <Button
        type="primary"
        loading={saving}
        disabled={!selectedUserUid || loadingUser}
        onClick={handleSave}
      >
        บันทึก
      </Button>
    </Space>
  );
};

const AddScopeModal = ({
  open,
  token,
  existingNames,
  onClose,
  onCreated,
}: {
  open: boolean;
  token: string;
  existingNames: string[];
  onClose: () => void;
  onCreated: () => void;
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const success = await createScope(values.name.trim(), values.description || '', token);
      setSaving(false);
      if (success) {
        toast.success('เพิ่ม scope สำเร็จ');
        form.resetFields();
        onCreated();
      } else {
        toast.warning('เพิ่ม scope ไม่สำเร็จ (ชื่ออาจซ้ำ)');
      }
    } catch (error) {
      // validation error — keep modal open
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="เพิ่ม scope ใหม่"
      visible={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={saving}
      okText="บันทึก"
      cancelText="ยกเลิก"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="ชื่อ scope"
          name="name"
          rules={[
            { required: true, message: 'กรุณากรอกชื่อ scope' },
            { max: 50, message: 'ความยาวไม่เกิน 50 ตัวอักษร' },
            {
              validator: (_, value) =>
                value && existingNames.includes(value.trim())
                  ? Promise.reject(new Error('ชื่อ scope นี้มีอยู่แล้ว'))
                  : Promise.resolve(),
            },
          ]}
        >
          <Input placeholder="เช่น CUSTOM-GAME-PAGE" />
        </Form.Item>
        <Form.Item label="คำอธิบาย" name="description">
          <Input.TextArea rows={2} placeholder="คำอธิบาย (ไม่บังคับ)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const EmployeeScope = () => {
  const { token = '', role = '' } = useRecoilValue(authState);
  const { userId = '' } = useRecoilValue(authInfoState);
  const [scopes, setScopes] = useState<ScopeItem[]>([]);
  const [openAddScopeModal, setOpenAddScopeModal] = useState(false);

  const refreshScopes = () => {
    getAllScopes(token).then((data) => {
      setScopes(data || []);
    });
  };

  useEffect(() => {
    refreshScopes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div id="EmployeeScopePage">
      <PageHeader
        title=" "
        extra={(
          <Button
            type="primary"
            size="large"
            style={{ float: 'right', paddingLeft: '1rem', paddingRight: '1rem' }}
            onClick={() => setOpenAddScopeModal(true)}
          >
            <Space>
              <Icon name="outline-add-1" size="18px" />
              เพิ่ม scope
            </Space>
          </Button>
        )}
      />
      <DeskPanel
        body={(
          <Tabs defaultActiveKey="position">
            <TabPane tab="ตามตำแหน่ง" key="position">
              <ByPositionTab scopes={scopes} token={token} currentRole={role} />
            </TabPane>
            <TabPane tab="รายบุคคล" key="employee">
              <ByEmployeeTab scopes={scopes} token={token} currentUserUid={userId} />
            </TabPane>
          </Tabs>
        )}
      />
      <AddScopeModal
        open={openAddScopeModal}
        token={token}
        existingNames={scopes.map((s) => s.name)}
        onClose={() => setOpenAddScopeModal(false)}
        onCreated={() => {
          setOpenAddScopeModal(false);
          refreshScopes();
        }}
      />
    </div>
  );
};
